import { STORAGE_BUCKET } from './supabase'

function throwIfError(result) {
  if (result.error) throw result.error
  return result.data
}

function safeFileName(name) {
  return name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(-120) || 'attachment'
}

function makeId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function normalizeMonthStarts(values, fallback) {
  const monthStarts = Array.isArray(values) ? values : []
  const normalized = [...new Set([...monthStarts, fallback].filter(Boolean))]
    .map((value) => String(value).slice(0, 10))
    .filter((value) => /^\d{4}-\d{2}-01$/.test(value))
    .sort()

  return normalized.length ? normalized : [fallback]
}

function normalizeArtifact(artifact) {
  if (!artifact) return artifact
  return {
    ...artifact,
    month_starts: normalizeMonthStarts(artifact.month_starts, artifact.month_start),
  }
}

function normalizeResource(resource) {
  if (!resource) return resource
  return {
    ...resource,
    start_date: resource.start_date || null,
    vendor_name: resource.vendor_name || null,
    comments: resource.comments || null,
    resume_label: resource.resume_label || null,
    resume_storage_path: resource.resume_storage_path || null,
    resume_uploaded_at: resource.resume_uploaded_at || null,
  }
}

function isMissingMonthStartsColumn(error) {
  return Boolean(
    error &&
      (error.code === '42703' ||
        error.code === 'PGRST204' ||
        String(error.message || '').includes('month_starts')),
  )
}

function isMissingResourceProfileColumn(error) {
  const message = String(error?.message || '')
  return Boolean(
    error &&
      (error.code === '42703' || error.code === 'PGRST204') &&
      ['start_date', 'vendor_name', 'comments', 'resume_label', 'resume_storage_path', 'resume_uploaded_at']
        .some((column) => message.includes(column)),
  )
}

function resourceProfileMigrationError() {
  return new Error(
    'Resource profiles are not enabled in Supabase yet. Run supabase/migrate-v1.6-resource-profiles.sql, then try again.',
  )
}

const BASE_RESOURCE_FIELDS = 'id, name, role, email, active, sort_order, created_at'
const RESOURCE_FIELDS = `${BASE_RESOURCE_FIELDS}, start_date, vendor_name, comments, resume_label, resume_storage_path, resume_uploaded_at`
const ARTIFACT_FIELDS =
  'id, resource_id, month_start, month_starts, kind, label, storage_path, external_url, created_at'

async function loadResources(client) {
  let result = await client
    .from('resources')
    .select(RESOURCE_FIELDS)
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })

  // Keep the workspace readable while the v1.6 migration is being applied.
  // Adding or editing profile fields still requires the migration.
  if (result.error && isMissingResourceProfileColumn(result.error)) {
    result = await client
      .from('resources')
      .select(BASE_RESOURCE_FIELDS)
      .eq('active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true })
  }

  return (throwIfError(result) || []).map(normalizeResource)
}

function resourceRecord(payload) {
  return {
    name: payload.name.trim(),
    role: payload.role?.trim() || null,
    email: payload.email?.trim() || null,
    start_date: payload.startDate || null,
    vendor_name: payload.vendorName?.trim() || null,
    comments: payload.comments?.trim() || null,
  }
}

export async function getMembership(client, email) {
  const result = await client
    .from('app_members')
    .select('email, display_name, is_admin')
    .ilike('email', email)
    .maybeSingle()

  return throwIfError(result)
}

export function createSupabaseService(client, user) {
  async function uploadResume(resourceId, file) {
    const path = `resources/${resourceId}/resume/${makeId()}-${safeFileName(file.name)}`
    const uploadResult = await client.storage.from(STORAGE_BUCKET).upload(path, file, {
      upsert: false,
      contentType: file.type || undefined,
    })
    throwIfError(uploadResult)
    return path
  }

  async function removeStoragePath(path) {
    if (!path) return
    const result = await client.storage.from(STORAGE_BUCKET).remove([path])
    if (result.error) console.warn('Could not remove old resource file:', result.error.message)
  }

  return {
    async loadMonth(monthStart, range) {
      const [loadedResources, entryResult] = await Promise.all([
        loadResources(client),
        client
          .from('timesheet_entries')
          .select('id, resource_id, work_date, status, hours, note, updated_at')
          .gte('work_date', range.first)
          .lte('work_date', range.last),
      ])

      let artifactResult = await client
        .from('timesheet_artifacts')
        .select(ARTIFACT_FIELDS)
        .contains('month_starts', [monthStart])
        .order('created_at', { ascending: false })

      // This fallback lets the old single-month schema keep loading while the
      // v1.5 migration is being applied. Multi-month uploads still require the migration.
      if (artifactResult.error && isMissingMonthStartsColumn(artifactResult.error)) {
        artifactResult = await client
          .from('timesheet_artifacts')
          .select(
            'id, resource_id, month_start, kind, label, storage_path, external_url, created_at',
          )
          .eq('month_start', monthStart)
          .order('created_at', { ascending: false })
      }

      return {
        resources: loadedResources,
        entries: throwIfError(entryResult) || [],
        artifacts: (throwIfError(artifactResult) || []).map(normalizeArtifact),
      }
    },

    async saveEntry(payload) {
      if (!payload.status) {
        const result = await client
          .from('timesheet_entries')
          .delete()
          .eq('resource_id', payload.resource_id)
          .eq('work_date', payload.work_date)
        throwIfError(result)
        return null
      }

      const record = {
        resource_id: payload.resource_id,
        work_date: payload.work_date,
        status: payload.status,
        hours: payload.status === 'worked' ? Number(payload.hours) : null,
        note: payload.note?.trim() || null,
        updated_by: user.id,
      }

      const result = await client
        .from('timesheet_entries')
        .upsert(record, { onConflict: 'resource_id,work_date' })
        .select('id, resource_id, work_date, status, hours, note, updated_at')
        .single()

      return throwIfError(result)
    },

    async addResource(payload) {
      const insertResult = await client
        .from('resources')
        .insert({
          ...resourceRecord(payload),
          created_by: user.id,
        })
        .select(RESOURCE_FIELDS)
        .single()

      if (insertResult.error) {
        if (isMissingResourceProfileColumn(insertResult.error)) throw resourceProfileMigrationError()
        throw insertResult.error
      }

      let resource = normalizeResource(insertResult.data)
      if (!payload.resumeFile) return resource

      let resumePath
      try {
        resumePath = await uploadResume(resource.id, payload.resumeFile)
        const updateResult = await client
          .from('resources')
          .update({
            resume_label: payload.resumeFile.name,
            resume_storage_path: resumePath,
            resume_uploaded_at: new Date().toISOString(),
          })
          .eq('id', resource.id)
          .select(RESOURCE_FIELDS)
          .single()

        if (updateResult.error) {
          if (isMissingResourceProfileColumn(updateResult.error)) throw resourceProfileMigrationError()
          throw updateResult.error
        }
        resource = normalizeResource(updateResult.data)
        return resource
      } catch (error) {
        if (resumePath) await removeStoragePath(resumePath)
        await client.from('resources').delete().eq('id', resource.id)
        throw error
      }
    },

    async updateResource(resource, payload) {
      let newResumePath = null
      const oldResumePath = resource.resume_storage_path || null

      try {
        if (payload.resumeFile) newResumePath = await uploadResume(resource.id, payload.resumeFile)

        const updates = resourceRecord(payload)
        if (newResumePath) {
          updates.resume_label = payload.resumeFile.name
          updates.resume_storage_path = newResumePath
          updates.resume_uploaded_at = new Date().toISOString()
        } else if (payload.removeResume) {
          updates.resume_label = null
          updates.resume_storage_path = null
          updates.resume_uploaded_at = null
        }

        const result = await client
          .from('resources')
          .update(updates)
          .eq('id', resource.id)
          .select(RESOURCE_FIELDS)
          .single()

        if (result.error) {
          if (isMissingResourceProfileColumn(result.error)) throw resourceProfileMigrationError()
          throw result.error
        }

        if (oldResumePath && (newResumePath || payload.removeResume)) {
          await removeStoragePath(oldResumePath)
        }

        return normalizeResource(result.data)
      } catch (error) {
        if (newResumePath) await removeStoragePath(newResumePath)
        throw error
      }
    },

    async getResourceResumeUrl(resource) {
      if (!resource?.resume_storage_path) return null
      const result = await client.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(resource.resume_storage_path, 10 * 60)
      const data = throwIfError(result)
      return data?.signedUrl || null
    },

    async uploadArtifact({ resourceId, monthStart, monthStarts, file }) {
      const assignedMonths = normalizeMonthStarts(monthStarts, monthStart)
      const primaryMonth = assignedMonths.includes(monthStart) ? monthStart : assignedMonths[0]
      const path = `${resourceId}/${primaryMonth.slice(0, 7)}/${makeId()}-${safeFileName(file.name)}`
      const uploadResult = await client.storage.from(STORAGE_BUCKET).upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      })
      throwIfError(uploadResult)

      const insertResult = await client
        .from('timesheet_artifacts')
        .insert({
          resource_id: resourceId,
          month_start: primaryMonth,
          month_starts: assignedMonths,
          kind: 'file',
          label: file.name,
          storage_path: path,
          created_by: user.id,
        })
        .select(ARTIFACT_FIELDS)
        .single()

      if (insertResult.error) {
        await client.storage.from(STORAGE_BUCKET).remove([path])
        if (isMissingMonthStartsColumn(insertResult.error)) {
          throw new Error(
            'Multi-month evidence is not enabled in Supabase yet. Run supabase/migrate-v1.5-multi-month-evidence.sql, then try again.',
          )
        }
        throw insertResult.error
      }

      return normalizeArtifact(insertResult.data)
    },

    async deleteArtifact(artifact) {
      if (artifact.storage_path) {
        const storageResult = await client.storage
          .from(STORAGE_BUCKET)
          .remove([artifact.storage_path])
        throwIfError(storageResult)
      }

      const result = await client.from('timesheet_artifacts').delete().eq('id', artifact.id)
      throwIfError(result)
    },

    async getArtifactUrl(artifact) {
      if (artifact.kind === 'link') return artifact.external_url
      if (!artifact.storage_path) return null

      const result = await client.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(artifact.storage_path, 10 * 60)

      const data = throwIfError(result)
      return data?.signedUrl || null
    },
  }
}
